"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { getNavLoading, subscribeNavLoading } from "@/components/navLoadingStore";

/**
 * One dropdown in the board's filter bar. The trigger and open/close state
 * are client-side; the panel CONTENT is server-rendered toggle links passed
 * as children, so selections navigate and every count stays live. The
 * component instance survives those soft navigations, which keeps the panel
 * open while the user ticks several values.
 *
 * A row click's "selected" state only updates once the new RSC payload lands
 * (a 2-3s round trip per ResultsLoadingOverlay's own comment), and nothing in
 * the open panel showed that — PostHog recorded rageclick on these exact
 * labels (Location, Category, Organization, Focus area) 30+ times/30d, the
 * classic "did my click register?" repeat-click. Dimming the panel from the
 * same navLoadingStore the results card already uses gives the same
 * in-flight feedback right where the user is looking.
 */
export function FilterDropdown({
  label,
  activeCount = 0,
  children,
  align = "left",
  wide = false,
  xl = false,
}: {
  label: string;
  activeCount?: number;
  children: ReactNode;
  /** Panel edge to pin — use "right" for dropdowns near the viewport edge. */
  align?: "left" | "right";
  wide?: boolean;
  /**
   * Mega panel (Location): spans the whole filter bar, which must be the
   * nearest positioned ancestor (`relative`). Content manages its own scroll.
   */
  xl?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // "left" | "right" anchor to the trigger, or a px offset pinning the panel
  // to the viewport edge when neither anchor fits (small screens).
  const [anchor, setAnchor] = useState<"left" | "right" | number>("left");
  const ref = useRef<HTMLDivElement>(null);
  const [navLoading, setNavLoadingState] = useState(false);

  useEffect(() => {
    setNavLoadingState(getNavLoading());
    return subscribeNavLoading(setNavLoadingState);
  }, []);

  const toggle = () => {
    if (!open && ref.current && !xl) {
      const r = ref.current.getBoundingClientRect();
      const panelWidth = Math.min(xl ? 896 : wide ? 320 : 288, window.innerWidth - 32);
      const fitsLeft = r.left + panelWidth <= window.innerWidth - 16;
      const fitsRight = r.right - panelWidth >= 16;
      if (align === "right" && fitsRight) setAnchor("right");
      else if (fitsLeft) setAnchor("left");
      else if (fitsRight) setAnchor("right");
      else setAnchor(16 - r.left);
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = activeCount > 0;

  return (
    <div ref={ref} className={xl ? "" : "relative"}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`inline-flex h-9 items-center gap-1.5 rounded-md border bg-white px-3 text-[13px] font-medium transition-colors ${
          active
            ? "border-ink text-ink"
            : "border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:text-ink"
        }`}
      >
        {label}
        {active ? (
          <span className="flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-ink px-1 text-[10px] font-semibold tabular-nums text-white">
            {activeCount}
          </span>
        ) : null}
        {open && navLoading ? (
          <span
            aria-hidden
            className="h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-600"
          />
        ) : (
          <svg
            aria-hidden
            width="9"
            height="6"
            viewBox="0 0 9 6"
            fill="none"
            className={`text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`}
          >
            <path
              d="M1 1l3.5 3.5L8 1"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {open ? (
        <div
          style={!xl && typeof anchor === "number" ? { left: anchor } : undefined}
          className={`absolute top-full z-30 mt-2 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-[0_10px_30px_rgba(19,26,38,0.10)] ${
            xl
              ? "inset-x-0 max-h-[80vh] md:max-h-none md:overflow-visible"
              : `max-h-80 p-2 ${wide ? "w-80" : "w-72"} ${anchor === "right" ? "right-0" : anchor === "left" ? "left-0" : ""}`
          }`}
        >
          <div
            className={`transition-opacity duration-150 ${navLoading ? "pointer-events-none opacity-50" : "opacity-100"}`}
          >
            {children}
          </div>
        </div>
      ) : null}
    </div>
  );
}
